import { useState, useCallback } from 'react';
import { Editor, Text, Range, Transforms, Element as SlateElement } from 'slate';
import { Slate, Editable } from 'slate-react';
import ColorPicker from './ColorPicker';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { insertLink } from './SlateFunction';
import './RichTextEditor.css';

export default function RichTextEditor({ editor, editor2, value, value2, onChange, onChange2, placeholder, headerText, showFirst = true, showSecond = true }) {
    const [selection, setSelection] = useState(null);
    const [selection2, setSelection2] = useState(null);
    const [cursorMarks, setCursorMarks] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const [isFocused2, setIsFocused2] = useState(false);

    const isMarkActive = useCallback((format) => {        
        const { selection } = editor;
        if (!selection) return false;

        // Collapsed caret: rely on current marks
        if (Range.isCollapsed(selection) ) {
            const marks = Editor.marks(editor);
            return !!marks?.[format];
        }

        // Range selection: check any text node
        for (const [node] of Editor.nodes(editor, {
            at: selection,
            match: n => Text.isText(n),
            mode: 'all',
        })) {
            if (node[format]) return true;
        }
        return false;
    }, [editor]);

    const isMarkActive2 = useCallback((format) => {        
        const { selection } = editor2;
        if (!selection) return false;
        // Collapsed caret: rely on current marks
        if (Range.isCollapsed(selection) ) {
            const marks = Editor.marks(editor2);
            return !!marks?.[format];
        }
        // Range selection: check any text node
        for (const [node] of Editor.nodes(editor2, {
            at: selection,
            match: n => Text.isText(n),
            mode: 'all',
        })) {
            if (node[format]) return true;
        }
        return false;
    }, [editor2]);

    const toggleMark = useCallback((format) => {
        const active = isMarkActive(format);
        if (active) Editor.removeMark(editor, format);
        else Editor.addMark(editor, format, true);
        setCursorMarks(Editor.marks(editor) || {});
    }, [editor, isMarkActive]);

    const toggleMark2 = useCallback((format) => {
        const active = isMarkActive2(format);
        if (active) Editor.removeMark(editor2, format);
        else Editor.addMark(editor2, format, true);
        setCursorMarks(Editor.marks(editor2) || {});
    }, [editor2, isMarkActive2]);

    const handleChange = useCallback((newValue) => {        
        const marks = Editor.marks(editor);
        setCursorMarks(marks || {});
        if(onChange) {
            onChange(newValue);
        }      
        setSelection(editor.selection);          
    }, [editor, onChange]);    

    const handleChange2 = useCallback((newValue2) => {        
        const marks = Editor.marks(editor2);    
        setCursorMarks(marks || {});
        if(onChange2) {
            onChange2(newValue2);
        }

        setSelection2(editor2.selection);
    }, [editor2, onChange2]);

    const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
    const renderElement = useCallback((props) => <Element {...props} />, []);

    const handleKeyDown = (event, editorInstance) => {
        if (!editorInstance.selection) return;

        // Ctrl/Cmd + B for editor 1
        if (editorInstance === editor && (event.ctrlKey || event.metaKey) && event.key === 'b') {
            event.preventDefault();
            toggleMark('bold'); 
        }

        // Ctrl/Cmd + I for editor 1
        if (editorInstance === editor && (event.ctrlKey || event.metaKey) && event.key === 'i') {
            event.preventDefault();

            toggleMark('italic');
        }

        // Ctrl/Cmd + U for editor 1
        if (editorInstance === editor && (event.ctrlKey || event.metaKey) && event.key === 'u') {
            event.preventDefault();
            toggleMark('underline');
        }

        // 2nd editor shortcuts Crtl/Cmd + B
        if (editorInstance === editor2 && (event.ctrlKey || event.metaKey) && event.key === 'b') {
            event.preventDefault();
            toggleMark2('bold');
        }

        // 2nd editor shortcuts Crtl/Cmd + I
        if (editorInstance === editor2 && (event.ctrlKey || event.metaKey) && event.key === 'i') {
            event.preventDefault();
            toggleMark2('italic');
        }

        // 2nd editor shortcuts Crtl/Cmd + U
        if (editorInstance === editor2 && (event.ctrlKey || event.metaKey) && event.key === 'u') {
            event.preventDefault();
            toggleMark2('underline');
        }
    };

    const activeEditor = isFocused ? editor : isFocused2 ? editor2 : null;


    
    const isLinkActive = (editorInstance) => {
        const [link] = Editor.nodes(editorInstance, {
            match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
        });
        return !!link;
    };
    
    return (
        <div className="border border-gray-300 rounded-lg shadow-sm overflow-hidden">
            <div className="toolbar-popup">
            
                
            {(isFocused|| isFocused2) ? (
                <>
                <button
                    type="button"
                    className={`toolbar-button ${isMarkActive('bold') || isMarkActive2('bold') ? 'active' : ''}`}
                    title="Bold (Ctrl+B)"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        
                        if (isFocused) toggleMark('bold'); if (isFocused2) toggleMark2('bold'); 
                    }}>
                    {/* icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="currentColor"><path d="M272-200v-560h221q65 0 120 40t55 111q0 51-23 78.5T602-491q25 11 55.5 41t30.5 90q0 89-65 124.5T501-200H272Zm121-112h104q48 0 58.5-24.5T566-372q0-11-10.5-35.5T494-432H393v120Zm0-228h93q33 0 48-17t15-38q0-24-17-39t-44-15h-95v109Z" /></svg>
                </button>
                <button
                    type="button"
                    className={`toolbar-button ${isMarkActive('italic') || isMarkActive2('italic') ? 'active' : ''}`}
                    onMouseDown={(e) => { e.preventDefault(); if (isFocused) toggleMark('italic'); if (isFocused2) toggleMark2('italic'); }}
                    title="Italic (Ctrl+I)"
                >
                    {/* icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M200-200v-100h160l120-360H320v-100h400v100H580L460-300h140v100H200Z"/></svg>
                </button>                
                <button
                    type="button"
                    className={`toolbar-button ${isMarkActive('underline') || isMarkActive2('underline') ? 'active' : ''}`}
                    onMouseDown={(e) => { e.preventDefault(); if (isFocused) toggleMark('underline'); if (isFocused2) toggleMark2('underline'); }}
                    title="Underline (Ctrl+U)"
                >
                    {/* icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M240-144v-72h480v72H240Zm240-144q-96 0-148.5-59.4T279-504.86V-816h97.21v317.09q0 52.85 26.43 85.88Q429.07-380 480.03-380q50.97 0 77.39-33.03 26.41-33.03 26.41-85.88V-816H681v311.14q0 98.06-52.5 157.46Q576-288 480-288Z"/></svg>
                </button>
                
                <ColorPicker editor={activeEditor} marks={cursorMarks} />

                <button
                    type="button"
                    title="Insert Link"
                    className="toolbar-button"
                    onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();

                        const url = window.prompt('Enter the URL or Email (include http:// or https:// for links):');
                        if (!url) return;

                        const selectionText = Editor.string(activeEditor, activeEditor.selection); 
                        const linkText = selectionText || url;

                        const finalUrl = url.includes('@') ? `mailto:${url}` : url;

                        insertLink(activeEditor, { url: finalUrl, text: linkText });
                    }}
                >
                    <InsertLinkIcon />
                                    
                </button>

                <RemoveLinkButton editor={activeEditor} disabled={!isLinkActive(activeEditor)} />
                </>) 
                
            : (null)}
    
            </div>

            <div className="rich-text-editors-container">
                {showFirst && (
                <>
                <p style={{marginTop: "2px", marginBottom: "5px", fontWeight: "bold", textDecoration: "underline"}}>{headerText ? "Header Text:" : "Acceptable Proof:"}</p>
                
                <Slate contentEditable="true" editor={editor} initialValue={value} onChange={handleChange}>
                    
                <Editable 
                    contentEditable="true"
                    title="Acceptable Proof"
                    onKeyDown={(event) => handleKeyDown(event, editor)}
                    onFocus={() => {setIsFocused(true); setIsFocused2(false); if (selection) {
                        Transforms.select(editor, selection);
                    }}}
                    onBlur={() => {
                        // editor lost focus
                        //console.log("Slate blurred");
                        Transforms.deselect(editor);
                        setIsFocused2(false);
                        setIsFocused(false);
                    }}  
                        style={{
                            border: "1px solid rgba(0,0,0,0.4)",
                            backgroundColor: isFocused ? "rgba(241, 241, 241, 1)" : "rgba(255, 255, 255, 1)",
                            borderRadius: "5px",
                            paddingLeft: "5px",
                            paddingBottom: "5px",
                            marginTop: "5px",
                            minHeight: "50px",
                            maxHeight: "400px",
                            overflowY: "auto",
                            overflowX: "hidden",
                            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
                        }}              
                        renderLeaf={renderLeaf}
                        renderElement={renderElement}
                        placeholder={placeholder}
                        
                        className="outline-none text-gray-700"
                    />
                </Slate>
                </>
                )}

                {showSecond && (
                <>
                <p style={{marginTop: "10px", marginBottom: "5px", fontWeight: "bold", textDecoration: "underline"}}>{headerText ? "Footer Text:" : "Additional Info:"}</p>
                <Slate contentEditable="true" editor={editor2} initialValue={value2} onChange={handleChange2}>
                    
                    <Editable 
                        contentEditable="true"
                        title="Additional Info"
                        onKeyDown={(event) => handleKeyDown(event, editor2)}
                        onFocus={() => {setIsFocused2(true); setIsFocused(false);if (selection2) {
                            Transforms.select(editor2, selection2);
                        }}}
                        onBlur={() => {
                            // editor lost focus
                            //console.log("Slate blurred");
                            Transforms.deselect(editor2);
                            setIsFocused(false);
                            setIsFocused2(false);
                        }}  
                        style={{
                            border: "1px solid rgba(0,0,0,0.4)",
                            backgroundColor: isFocused2 ? "rgba(241, 241, 241, 1)" : "rgba(255, 255, 255, 1)",
                            borderRadius: "5px",
                            paddingBottom: "5px",
                            paddingLeft: "5px",
                            marginTop: "5px",
                            minHeight: "50px",
                            maxHeight: "400px",
                            overflowY: "auto",
                            overflowX: "hidden",
                            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
                        }}                
                        renderLeaf={renderLeaf}
                        renderElement={renderElement}
                        placeholder={placeholder}
                        
                        className="outline-none text-gray-700"
                    />
                </Slate>
                </>
                )}
            </div>
        </div>
    );
}

export const Leaf = ({ attributes, children, leaf }) => {
    let style = {};
    if (leaf.bold) children = <strong>{children}</strong>;
    if (leaf.underline) children = <u>{children}</u>;
    if (leaf.italic) children = <em>{children}</em>;
    if (leaf.color) children = <span style={{ color: leaf.color}}>{children}</span>;
    if (leaf.fontSize) style.fontSize = leaf.fontSize;
    return <span {...attributes} style={style}>{children}</span>;
};


export const Element = ({ attributes, children, element }) => {
    switch (element.type) {
        case 'link':
            return (
                <a
                    {...attributes}
                    href={element.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className='text-[#307D88] underline hover:no-underline'
                >
                    {children}
                </a>
            )
        default:
            return <p {...attributes}>{children}</p>
    }
};

export const RemoveLinkButton = ({ editor, disabled }) => {
    const handleRemoveLink = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const { selection } = editor;

        // Hard stop if no selection or collapsed cursor
        if (!selection || Range.isCollapsed(selection)) return;

        // Ensure selection is inside a link
        const [linkEntry] = Editor.nodes(editor, {
            at: selection,
            match: n =>
                SlateElement.isElement(n) &&
                n.type === 'link',
        });

        if (!linkEntry) return;

        // Only unwrap the selected part of the link
        Transforms.unwrapNodes(editor, {
            at: selection,
            split: true,
            match: n =>
                SlateElement.isElement(n) &&
                n.type === 'link',
        });
    };

    return (
        <button
            type="button"
            className={`toolbar-button ${disabled ? 'disabled' : ''}`}
            title="Remove Link"
            onMouseDown={handleRemoveLink}
            disabled={disabled}
        >
            <LinkOffIcon />
        </button>
    );
};
